/**
 * V2 Wallet API Service
 */

import NetworkFetchError from '@/erros/NetworkFetchError'
import { apiInterceptor } from '@/networks/api/interceptor'
import { toPinFetchError } from '@/networks/api/v2/pin-error'

export interface IWalletV2 {
  walletId: string
  id?: string // V1 호환성을 위해 추가 (walletId와 동일)
  label: string
  createdAt: string
  updatedAt: string
  accounts?: any[] // V2에서는 계정 목록이 포함될 수 있음
}

export interface ICreateWalletRequest {
  UVD: IUVD
  wallet: {
    label?: string
    defaultWallet?: boolean
  }
  hint: IHint
  mnemonic?: string // Import wallet: 암호화된 mnemonic (optional)
  accounts?: Array<{
    currencyId: string
    accountIndex: number
    label?: string
  }>
}

export interface ICreateWalletResponse {
  walletId: string
  address?: string
}

/**
 * UVD (User Verification Data) 인터페이스
 */
export interface IUVD {
  b64SKey: string
  b64Data: string
}

/**
 * PIN Hint 인터페이스
 */
export interface IHint {
  data: string
  length: string
  version: number
}

/**
 * PIN 검증 응답
 */
export interface IVerifyPinResponse {
  status: string
  walletId: string
  pinVerified: boolean
}

/**
 * PIN 변경 응답
 */
export interface IChangePinResponse {
  status: string
  walletId: string
  message?: string
}

/**
 * PIN 재시도 리셋 응답
 */
export interface IResetPinRetriesResponse {
  walletId: string
  maxTryCnt: number
  remainTryCnt: number
  isRegistered: boolean
  registeredTime: string | null
  necessaryTime: string
}

/**
 * 지갑 백업 응답
 */
export interface IBackupWalletResponse {
  walletId: string
  mnemonic: string
  status: string
}

/**
 * 키 정보 응답 (keyinfo)
 */
export interface IKeyInfoResponse {
  maxTryCnt: number
  remainTryCnt: number
  isBlocked: boolean
  isRegistered: boolean
  registeredTime: string | null
  necessaryTime: string
}

export interface IWalletAPIServiceV2 {
  getWallets(): Promise<IWalletV2[]>
  getWallet(walletId: string): Promise<any>
  getKeyInfo(walletId: string): Promise<IKeyInfoResponse>
  createWallet(data: ICreateWalletRequest): Promise<ICreateWalletResponse>
  updateWallet(walletId: string, data: { label?: string }): Promise<any>
  verifyPin(walletId: string, uvd: IUVD): Promise<IVerifyPinResponse>
  changePin(
    walletId: string,
    uvd: IUVD,
    newUVD: IUVD,
    hint: IHint,
  ): Promise<IChangePinResponse>
  resetPinRetries(walletId: string): Promise<IResetPinRetriesResponse>
  backupWallet(walletId: string, uvd: IUVD): Promise<IBackupWalletResponse>
  deleteWallet(walletId: string, uvd: IUVD): Promise<IBackupWalletResponse>
  getAccountBalances(accountIds: string[]): Promise<any>
}

export class WalletAPIServiceV2 implements IWalletAPIServiceV2 {
  /**
   * 지갑 목록 조회
   * V2: GET /wallet
   */
  async getWallets(): Promise<any> {
    try {
      const response = await apiInterceptor.request('/wallet')
      if (!response.ok) {
        throw new Error(`Failed to fetch wallets: ${response.statusText}`)
      }

      const data = await response.json()

      // V2 API는 { success, data: { wallets: [...] }, message } 형태로 반환
      let result = data
      if (data.success && data.data) {
        result = data.data // wallets와 totalCount를 포함한 객체 반환
      }

      // 기본 지갑 ID 저장 (NFT API 등에서 필요)
      const wallets = result.wallets || result
      if (Array.isArray(wallets) && wallets.length > 0) {
        const defaultWallet = wallets.find((w) => w.defaultWallet) || wallets[0]
        if (defaultWallet?.id) {
          localStorage.setItem('defaultWalletId', defaultWallet.id)
        }
      }

      return result
    } catch (error) {
      throw error
    }
  }

  /**
   * 지갑 상세 조회
   * V2: GET /wallet/:walletId
   *
   * 지갑 정보와 함께 nonce를 반환 (UVD 생성에 필요)
   */
  async getWallet(walletId: string): Promise<any> {
    const response = await apiInterceptor.request(`/wallet/${walletId}`)

    if (!response.ok) {
      throw new Error(`Failed to fetch wallet: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 API 응답 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * 지갑 키 정보 조회 (PIN 잠금 상태 등)
   * V2: GET /wallet/:walletId (getWallet과 동일한 엔드포인트 사용)
   *
   * @param walletId - 지갑 ID
   * @returns PIN 시도 횟수, 잠금 상태 등의 정보
   */
  async getKeyInfo(walletId: string): Promise<IKeyInfoResponse> {
    return this.getWallet(walletId)
  }

  /**
   * 지갑 생성
   * V2: POST /wallet 또는 POST /wallet/create
   */
  async createWallet(
    data: ICreateWalletRequest,
  ): Promise<ICreateWalletResponse> {
    const response = await apiInterceptor.request('/wallet', {
      method: 'POST',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Failed to create wallet: ${response.statusText}`)
    }

    const result = await response.json()
    return result
  }

  /**
   * 지갑 수정 (라벨 등)
   * V2: PUT /wallet/:walletId
   */
  async updateWallet(walletId: string, data: { label?: string }): Promise<any> {
    const response = await apiInterceptor.request(`/wallet/${walletId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(`Failed to update wallet: ${response.statusText}`)
    }

    const result = await response.json()

    // V2 응답 구조 처리
    if (result.success && result.data) {
      return result.data
    }

    return result
  }

  /**
   * PIN 검증
   * V2: POST /wallet/:walletId/pin/verify
   *
   * @param walletId - 지갑 ID
   * @param uvd - User Verification Data (암호화된 PIN)
   */
  async verifyPin(walletId: string, uvd: IUVD): Promise<IVerifyPinResponse> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/pin/verify`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ UVD: uvd }),
      },
    )

    const data = await response.json()

    if (!response.ok) {
      // PIN_INVALID / WALLET_BLOCKED
      const pinError = toPinFetchError(data, response, 'POST')
      if (pinError) throw pinError

      // 에러 응답 구조: { success: false, error: { code, message, details: { remainPinTryCnt } } }
      const errorInfo = data.error || {}
      const errorMessage =
        errorInfo.message || `Failed to verify PIN: ${response.statusText}`

      // NetworkFetchError 형식으로 변환
      const apiResponse = {
        status: response.status,
        statusText: response.statusText,
        data: {
          message: {
            message: errorMessage,
            data: {
              RemainPinTryCnt: errorInfo.details?.remainPinTryCnt,
            },
          },
        },
      }

      throw new NetworkFetchError(apiResponse as any, errorMessage)
    }

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * PIN 변경
   * V2: PUT /wallet/:walletId/pin
   *
   * @param walletId - 지갑 ID
   * @param uvd - 현재 PIN의 UVD
   * @param newUVD - 새 PIN의 UVD
   * @param hint - PIN 힌트
   */
  async changePin(
    walletId: string,
    uvd: IUVD,
    newUVD: IUVD,
    hint: IHint,
  ): Promise<IChangePinResponse> {
    const response = await apiInterceptor.request(`/wallet/${walletId}/pin`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        UVD: uvd,
        newUVD: newUVD,
        hint: hint,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      // PIN_INVALID / WALLET_BLOCKED
      const pinError = toPinFetchError(data, response, 'PUT')
      if (pinError) throw pinError

      const errorInfo = data.error || {}
      const errorMessage =
        errorInfo.message || `Failed to change PIN: ${response.statusText}`

      const error: any = new Error(errorMessage)
      error.code = errorInfo.code
      error.details = errorInfo.details
      error.remainPinTryCnt = errorInfo.details?.remainPinTryCnt

      throw error
    }

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * PIN 재시도 횟수 초기화
   * V2: POST /wallet/:walletId/pin/reset-retries
   *
   * @param walletId - 지갑 ID
   */
  async resetPinRetries(walletId: string): Promise<IResetPinRetriesResponse> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/pin/reset-retries`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      },
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error?.message ||
          `Failed to reset PIN retries: ${response.statusText}`,
      )
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * 지갑 백업 (mnemonic 추출)
   * V2: POST /wallet/:walletId/backup
   *
   * 백업 후 지갑 상태가 'exported'로 변경됨
   *
   * @param walletId - 지갑 ID
   * @param uvd - User Verification Data
   */
  async backupWallet(
    walletId: string,
    uvd: IUVD,
  ): Promise<IBackupWalletResponse> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/backup`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ UVD: uvd }),
      },
    )

    const data = await response.json()

    if (!response.ok) {
      // PIN_INVALID / WALLET_BLOCKED
      const pinError = toPinFetchError(data, response, 'POST')
      if (pinError) throw pinError

      const errorInfo = data.error || {}
      const errorMessage =
        errorInfo.message || `Failed to backup wallet: ${response.statusText}`

      // PIN 계열이 아닌 오류. 화면이 'isInvalidPIN' in error로 분기하므로
      // 속성을 붙여 네트워크 오류 페이지로 흐르게 유지한다.
      const error: any = new Error(errorMessage)
      error.code = errorInfo.code
      error.details = errorInfo.details
      error.remainPinTryCnt = errorInfo.details?.remainPinTryCnt

      // NetworkFetchError가 기대하는 형식으로 변환
      error.response = {
        status: response.status,
        statusText: response.statusText,
        data: {
          message: {
            message: errorMessage,
            data: {
              RemainPinTryCnt: errorInfo.details?.remainPinTryCnt,
            },
          },
        },
      }
      error.data = error.response.data

      // NetworkFetchError의 getter 메서드들을 수동으로 구현
      error.reasonData = error.data
      // 판정 기준은 toPinFetchError와 동일하게 유지한다.
      // !== undefined로 두면 null이나 문자열도 통과해 헬퍼와 결과가 갈린다.
      error.isInvalidPIN =
        errorInfo.code === 'PIN_INVALID' &&
        typeof errorInfo.details?.remainPinTryCnt === 'number'
      error.pinTryCnt = errorInfo.details?.remainPinTryCnt

      throw error
    }

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * 지갑 삭제
   * V2: DELETE /wallet/:walletId
   *
   * ⚠️ 주의: 지갑은 'exported' 상태일 때만 삭제 가능 (백업 후)
   * ⚠️ DELETE 요청이지만 body에 UVD를 포함해야 함
   *
   * @param walletId - 지갑 ID
   * @param uvd - User Verification Data
   * @returns mnemonic과 함께 반환
   */
  async deleteWallet(
    walletId: string,
    uvd: IUVD,
  ): Promise<IBackupWalletResponse> {
    const response = await apiInterceptor.request(`/wallet/${walletId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ UVD: uvd }),
    })

    if (!response.ok) {
      const errorData = await response.json()

      // PIN_INVALID / WALLET_BLOCKED
      const pinError = toPinFetchError(errorData, response, 'DELETE')
      if (pinError) throw pinError

      throw new Error(
        errorData.error?.message ||
          `Failed to delete wallet: ${response.statusText}`,
      )
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * 계좌 밸런스 조회
   * V2: GET /wallet/account/balance?accountIds=xxx
   */
  async getAccountBalances(accountIds: string[]): Promise<any> {
    const queryParams = new URLSearchParams()
    accountIds.forEach((id) => queryParams.append('accountIds', id))

    const response = await apiInterceptor.request(
      `/wallet/account/balance?${queryParams.toString()}`,
    )
    if (!response.ok) {
      throw new Error(
        `Failed to fetch account balances: ${response.statusText}`,
      )
    }

    const data = await response.json()

    // V2 API 응답 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }
}

// 싱글톤 인스턴스
export const walletAPIServiceV2 = new WalletAPIServiceV2()
